/**
 * Cost Badge Component
 *
 * Displays the theoretical cost of a POS product with visual indicators.
 */

import { AlertCircle } from 'lucide-react';
import { Tooltip } from '@/components/ui';
import { formatCentimes } from '@/lib/format';

interface CostBadgeProps {
  /** Cost in cents */
  cost: number | null;
  /** Whether the product has a recipe */
  hasRecipe: boolean;
  /** Whether the cost is incomplete (missing PMP) */
  isIncomplete?: boolean;
  /** Number of missing PMP */
  missingPmpCount?: number;
  /** Currency code */
  currency?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

export function CostBadge({
  cost,
  hasRecipe,
  isIncomplete,
  missingPmpCount,
  currency = 'EUR',
  size = 'md',
}: CostBadgeProps) {
  // Format cost using currency
  const formatCost = (cents: number) => formatCentimes(cents, currency);

  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  // No recipe - show dash
  if (!hasRecipe) {
    return <span className={`${textSize} text-neutral-400`}>-</span>;
  }

  // Recipe but no cost calculated
  if (cost === null) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-600">
        Non calcule
      </span>
    );
  }

  // Cost available but incomplete
  if (isIncomplete) {
    return (
      <Tooltip content={`Cout incomplet - ${missingPmpCount ?? 'plusieurs'} PMP manquant(s)`}>
        <span className={`${textSize} font-medium text-warning-600 cursor-help flex items-center gap-1`}>
          {formatCost(cost)}*
          <AlertCircle className="w-3 h-3" />
        </span>
      </Tooltip>
    );
  }

  // Full cost available
  return (
    <span className={`${textSize} font-medium text-dark-500`}>
      {formatCost(cost)}
    </span>
  );
}
